import {createContext, useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";


export const AuthContext = createContext();

export function AuthContextProvider({children}) {

    const location = useLocation();
    const navigate = useNavigate();

    const [STATES] = useState(['PROFILE', 'LANDING', 'LANDINGCOOKIE', 'LOGINREGISTER', 'FORM']);

    const [type, setType] = useState(STATES[2]);
    const [user, setUser] = useState(null);
    const [isAuthLoading, setIsAuthLoading] = useState(true);
    const [suspendRedirects, setSuspendRedirects] = useState(false);

    // try to refresh using refresh token cookie
    const tryRefresh = async () => {
        try {
            const res = await fetch(process.env.REACT_APP_API + '/branduser/refresh-token', {
                method: 'POST',
                credentials: 'include',
            });
            return res.ok;
        } catch (err) {
            console.error('refresh error', err);
            return false;
        }
    };

    // fetch current user and return the user object (or null)
    // opts.silent = true => don't toggle global isAuthLoading (used for background polling)
    const me = async (opts = { silent: false }) => {
        if (!opts.silent) setIsAuthLoading(true);
        try {
            const res = await fetch(process.env.REACT_APP_API + '/branduser/me', {
                method: 'GET',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
            });
            console.debug('me() response status', res.status);
            if (res.status === 401) {
                // try refresh once
                const refreshed = await tryRefresh();
                if (refreshed) {
                    // retry
                    const retry = await fetch(process.env.REACT_APP_API + '/branduser/me', {
                        method: 'GET',
                        headers: { 'Content-Type': 'application/json' },
                        credentials: 'include',
                    });
                    if (!retry.ok) {
                        setUser(null);
                        if (!opts.silent) setIsAuthLoading(false);
                        return null;
                    }
                    const data2 = await retry.json();
                    const bu2 = data2?.brandUser || null;
                    setUser(bu2);
                    if (!opts.silent) setIsAuthLoading(false);
                    return bu2;
                }
                setUser(null);
                if (!opts.silent) setIsAuthLoading(false);
                return null;
            }
            if (!res.ok) {
                setUser(null);
                if (!opts.silent) setIsAuthLoading(false);
                return null;
            }
            const data = await res.json();
            const bu = data?.brandUser || null;
            setUser(bu);
            if (!opts.silent) setIsAuthLoading(false);
            return bu;
        } catch (err) {
            console.error('me() error', err);
            setUser(null);
            if (!opts.silent) setIsAuthLoading(false);
            return null;
        }
    };

    useEffect(() => {
        let cancelled = false;

        (async () => {
            const currentUser = await me();

            // if effect has been cancelled (component unmounted), do nothing
            if (cancelled) return;

            const pathname = location.pathname;
            console.debug('AuthContext: pathname=', pathname, 'currentUser=', currentUser);

            // helper to navigate only when necessary (use replace to avoid polluting history)
            const safeNavigate = (to) => {
                if (pathname !== to && !suspendRedirects) navigate(to, { replace: true });
            };

            // treat certain paths as public (no redirect back to landing)
            const publicPaths = new Set(['/', '/login', '/register', '/verify-email-info']);
            if (publicPaths.has(pathname)) {
                // keep landing/state behaviour for known public paths
                if (pathname === '/') {
                    setType(currentUser ? STATES[2] : STATES[1]);
                    return;
                } else if (pathname === '/login' || pathname === '/register') {
                    if (currentUser) {
                        setType(STATES[2]);
                        safeNavigate('/dashboard');
                    } else {
                        setType(STATES[3]);
                    }
                } else if (pathname === '/verify-email-info') {
                    // verification info should be accessible even if not authenticated
                    setType(STATES[1]);
                }
                return;
            }

            if (pathname === "/form") {
                if (!currentUser) {
                    setType(STATES[1]);
                    safeNavigate('/');
                    return;
                }
                // if already activated, redirect to landing
                if (currentUser.state === 'activated') {
                    setType(STATES[1]);
                    safeNavigate('/');
                    return;
                }
                setType(STATES[4]);
                return;
            }

            if (pathname === "/finished") {
                if (!currentUser) {
                    setType(STATES[1]);
                    safeNavigate('/');
                    return;
                }
                // if already activated, redirect to landing
                if (currentUser.state === 'activated') {
                    setType(STATES[1]);
                    safeNavigate('/');
                    return;
                }
                setType(STATES[4]);
                return;
            }


            if (pathname === "/archived") {
                if (!currentUser) {
                    setType(STATES[1]);
                    safeNavigate('/');
                    return;
                }
                // if already activated, redirect to landing
                if (currentUser.state === 'activated') {
                    setType(STATES[1]);
                    safeNavigate('/');
                    return;
                }
                setType(STATES[4]);
                return;
            }



            // other protected pages
            if (currentUser) {
                if (currentUser.state === 'notfinished') {
                    setType(STATES[1]);
                    safeNavigate('/form');
                    return;
                } else if (currentUser.state === 'finished') {
                    setType(STATES[1]);
                    safeNavigate('/finished');
                    return;
                } if (currentUser.state === 'archived') {
                    setType(STATES[1]);
                    safeNavigate('/archived');
                    return;
                }
                setType(STATES[0]);
            } else {
                setType(STATES[1]);
                safeNavigate('/');
            }
            console.debug('AuthContext decision after public checks, path=', pathname);
        })();

        return () => { cancelled = true; };
    }, [location.pathname, navigate, STATES]);

    const logout = async () => {
        try {
            await fetch(process.env.REACT_APP_API + '/branduser/logout', {
                method: 'POST',
                credentials: 'include',
            });
        } catch (err) {
            console.error('logout error', err);
        } finally {
            setUser(null);
            setType(STATES[1]);
            navigate('/', { replace: true });
        }
    };


    return (
        <AuthContext.Provider
            value={{
                type,
                setType,
                STATES,
                user,
                setUser,
                isAuthLoading,
                me,
                tryRefresh,
                suspendRedirects,
                setSuspendRedirects,
                logout,
            }}>
            {children}
        </AuthContext.Provider>
    )
}